import { Box, Theme, Typography, useTheme } from '@mui/material';
import React, {
    Dispatch,
    memo,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    DateString,
    getUniqueYearsFromDateStrings,
    SimulationOnlyData,
} from 'routes/HumanResourcesModule/business/types';
import { extractDates } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import EditFieldCellRenderer from 'ui/AgGridReact/CellRenderers/EditFieldCellRenderer';
import { SIMULATED_AMOUNT } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { getPivotedField } from 'routes/HumanResourcesModule/helpers/dataHelpers';
import { getMonthList } from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { SPLIT_BY } from 'routes/BudgetModule/config/constants';
import { EnvelopeHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/helpers/types';
import { AgentHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/types';
import { camelize } from 'helpers/syntaxHelper';
import { QueryKey } from '@tanstack/react-query';
import { capitalize } from 'helpers/helpers';
import { getScopedT } from 'helpers/translationHelpers';
import ValueByDateGridLoader from './ValueByDateGridLoader';
import { useGetHrSimulationPreviousData } from './helpers/hooks/valueByDate';
import { getPreviousSimulationColDefs } from './helpers/colDefinitions';
import {
    getPreviousSimulationData,
    getPreviousSimulationDataForModal,
    PreviousDataFilters,
    keepOnlyChangedDates,
    PREVIOUS_SIMULATION_TYPE_OF_LINE,
} from './helpers/dataHelpers';
import ValueByDateGrid from './ValueByDateGrid';
import { useTranslation } from 'react-i18next';

const getCellColorSelector =
    (
        palette: Theme['palette'],
        valuesByDates: Record<DateString, number>,
        previousSimulationDataByYear: SimulationOnlyData,
    ) =>
    (d: DateString) => {
        const isHigher =
            valuesByDates[d] >
            (previousSimulationDataByYear[
                getPivotedField(SIMULATED_AMOUNT, d)
            ] ?? 0);
        const isLower =
            valuesByDates[d] <
            (previousSimulationDataByYear[
                getPivotedField(SIMULATED_AMOUNT, d)
            ] ?? 0);
        if (isHigher) return palette.error.main;
        if (isLower) return palette.success.main;
        return palette.text.primary;
    };

type Props = {
    hrSimulation: HrSimulation;
    filters: PreviousDataFilters;
    setHasChanges: Dispatch<SetStateAction<boolean>>;
    onValuesByDatesChange: (newValues: Record<DateString, number>) => void;
    apiCacheKey: QueryKey;
    endpoint: string;
    isNonRecurrentEnvelope?: boolean;
    translationKey: string;
    hypothesis?: EnvelopeHypothesis | AgentHypothesis | null;
    isUpdateEnabled?: boolean;
};

const PreviousSimulationDataGrid = ({
    hrSimulation,
    filters,
    setHasChanges,
    onValuesByDatesChange,
    hypothesis = null,
    isNonRecurrentEnvelope = false,
    apiCacheKey,
    endpoint,
    translationKey,
    isUpdateEnabled = true,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const tScoped = getScopedT(translationKey, t);

    const {
        startDate: simulationStart,
        endDate: simulationEnd,
        type: simulationType,
    } = hrSimulation;

    const {
        data: previousSimulationDataResponse,
        isLoading: isPreviousSimulationDataLoading,
    } = useGetHrSimulationPreviousData(apiCacheKey, endpoint);

    const previousSimulationData = previousSimulationDataResponse?.data ?? [];

    const previousSimulationDataByYear = getPreviousSimulationDataForModal({
        previousSimulationData,
        filters,
    });

    const initialValuesByDates: Record<DateString, number> = useMemo(
        () => ({
            ...Object.fromEntries(
                getMonthList(simulationStart, simulationEnd, false).map(
                    (date) => [
                        date,
                        previousSimulationDataByYear?.[
                            `${SIMULATED_AMOUNT}${SPLIT_BY}${date}`
                        ] ?? 0,
                    ],
                ),
            ),
            ...hypothesis?.newValuesByDates,
        }),
        [
            JSON.stringify(hypothesis?.newValuesByDates),
            JSON.stringify(previousSimulationDataByYear),
            simulationEnd,
            simulationStart,
        ],
    );

    const [valuesByDates, setValuesByDates] = useState(initialValuesByDates);

    useEffect(() => {
        if (!isPreviousSimulationDataLoading) {
            setValuesByDates(initialValuesByDates);
        }
    }, [isPreviousSimulationDataLoading, JSON.stringify(initialValuesByDates)]);

    useEffect(() => {
        setHasChanges(
            Object.keys(valuesByDates).some(
                (k) => valuesByDates[k] !== initialValuesByDates[k],
            ),
        );
    }, [
        JSON.stringify(initialValuesByDates),
        setHasChanges,
        JSON.stringify(valuesByDates),
    ]);

    const setValueOfDate = (date: DateString, value: number) => {
        const newValues = {
            ...valuesByDates,
            [date]: value || 0,
        };
        setValuesByDates(newValues);
        // We need to save in the hypothesis only the dates for which the user
        // has changed the values. Otherwise, if we save all the dates of the
        // hrSimulation the user can change other hypotheses prior to envelope
        // hypotheses and we might end up with a hypothesis that seems to affect
        // all months when the user really wanted to affect only one month.
        onValuesByDatesChange(
            keepOnlyChangedDates(previousSimulationDataByYear, newValues),
        );
    };

    const previousSimulationDates = extractDates<DateString>([
        previousSimulationDataByYear,
    ]);
    const previousSimulationYears = getUniqueYearsFromDateStrings(
        previousSimulationDates,
    );

    const colDefs = getPreviousSimulationColDefs({
        simulationStart,
        simulationEnd,
        setValueOfDate,
        cellColorSelector: getCellColorSelector(
            theme.palette,
            valuesByDates,
            previousSimulationDataByYear,
        ),
        isUpdateEnabled,
        translationKey,
    });
    const simulatedDataTitle = isUpdateEnabled
        ? capitalize(
              tScoped('simulatedDataEdit', {
                  type: t(
                      `humanResources.simulation.${camelize(simulationType)}`,
                  ),
              }),
          )
        : tScoped('simulatedDataResume', {
              years: previousSimulationYears.join(', '),
          });

    const previousSimulationDataSplitted = getPreviousSimulationData(
        previousSimulationDataByYear,
        valuesByDates,
    );

    return (
        <>
            <Typography variant="h4">{simulatedDataTitle}</Typography>
            <Typography sx={{ pt: '0.6rem' }}>
                {tScoped(
                    `simulatedDataExplanation1${isNonRecurrentEnvelope ? 'NonRecurrent' : ''}`,
                )}
            </Typography>
            {isUpdateEnabled && (
                <Typography sx={{ pt: '0.6rem' }}>
                    {tScoped(
                        `simulatedDataExplanation2${isNonRecurrentEnvelope ? 'NonRecurrent' : ''}`,
                    )}
                </Typography>
            )}
            {isPreviousSimulationDataLoading ? (
                <ValueByDateGridLoader />
            ) : (
                <Box
                    sx={{
                        mt: '1em',
                        // with ag-theme-material and autoHeight, there is a 150px min-height by default, which is too much here
                        '& .ag-center-cols-viewport': {
                            minHeight: '3rem !important',
                        },
                    }}
                >
                    <ValueByDateGrid
                        className="ag-theme-material"
                        headerHeight={36}
                        rowHeight={36}
                        domLayout="autoHeight"
                        getRowId={({ data }) =>
                            data[PREVIOUS_SIMULATION_TYPE_OF_LINE]
                        }
                        rowData={previousSimulationDataSplitted}
                        columnDefs={colDefs}
                        components={{
                            editFieldCellRenderer: isUpdateEnabled
                                ? EditFieldCellRenderer
                                : undefined,
                        }}
                        defaultColDef={{
                            suppressMovable: true,
                            suppressHeaderMenuButton: true,
                            width: 120,
                        }}
                    />
                </Box>
            )}
        </>
    );
};
export default memo(PreviousSimulationDataGrid);
